import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { alsDatum, alsDauer, api, wochentagKurz } from '../api/client'

export interface KursUebersicht {
  id: number
  kursnummer: string
  verkehrstagtyp: string
  verkehrstagtypText: string | null
  gueltigAb: string | null
  gueltigBis: string | null
  nurAnEinemTag: boolean
  quellblatt: string | null
  quelldateiId: number | null
  wochentage: string[]
  arbeitszeitProWoche: number | null
  anzahlFahrten: number
  geteilterDienst: boolean
  hatAbweichung: boolean
  mappeVorhanden: boolean
}

interface Seite {
  content: KursUebersicht[]
  totalElements: number
}

export function Kurse() {
  const [kurse, setKurse] = useState<KursUebersicht[]>([])
  const [gesamt, setGesamt] = useState(0)
  const [suche, setSuche] = useState('')
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const navigation = useNavigate()

  useEffect(() => {
    let abgebrochen = false
    // Kurze Verzögerung, damit nicht bei jedem Tastendruck eine Anfrage startet.
    const zeitgeber = setTimeout(async () => {
      setLaedt(true)
      try {
        const pfad = suche.trim()
          ? `/api/kurse?suche=${encodeURIComponent(suche.trim())}&groesse=200`
          : '/api/kurse?groesse=200'
        const seite = await api.get<Seite>(pfad)
        if (!abgebrochen) {
          setKurse(seite.content)
          setGesamt(seite.totalElements)
          setFehler(null)
        }
      } catch (ex) {
        if (!abgebrochen) setFehler(ex instanceof Error ? ex.message : 'Laden fehlgeschlagen')
      } finally {
        if (!abgebrochen) setLaedt(false)
      }
    }, 250)

    return () => {
      abgebrochen = true
      clearTimeout(zeitgeber)
    }
  }, [suche])

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Kurse</h1>
          <p>Fahrerdienste aus den eingelesenen Kursblättern</p>
        </div>
      </div>

      {fehler && <div className="meldung fehler">{fehler}</div>}

      <div className="werkzeugleiste">
        <input
          type="text"
          placeholder="Kursnummer suchen …"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
        />
        <span style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
          {gesamt} {gesamt === 1 ? 'Kurs' : 'Kurse'}
        </span>
      </div>

      <div className="tabelle-huelle">
        {laedt ? (
          <div className="laden">Wird geladen …</div>
        ) : kurse.length === 0 ? (
          <div className="leer">
            Keine Kurse vorhanden. Laden Sie in der Kursbibliothek ein Kursblatt hoch.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Kurs</th>
                <th>Verkehrstage</th>
                <th>Gültig ab</th>
                <th>Arbeitszeit/Woche</th>
                <th>Fahrten</th>
                <th>Merkmale</th>
                <th>Quelle</th>
              </tr>
            </thead>
            <tbody>
              {kurse.map((kurs) => (
                <tr
                  key={kurs.id}
                  className="klickbar"
                  onClick={() => navigation(`/kurse/${kurs.id}`)}
                >
                  <td>
                    <strong>{kurs.kursnummer}</strong>
                  </td>
                  <td>
                    {kurs.wochentage.map(wochentagKurz).join(' ') || '–'}
                    {kurs.verkehrstagtypText && (
                      <span style={{ color: 'var(--grau-500)', marginLeft: 8, fontSize: '0.82rem' }}>
                        {kurs.verkehrstagtypText}
                      </span>
                    )}
                  </td>
                  <td className="zahl">
                    {alsDatum(kurs.gueltigAb)}
                    {kurs.nurAnEinemTag && <span className="etikett navy" style={{ marginLeft: 6 }}>nur an diesem Tag</span>}
                  </td>
                  <td className="zahl">{alsDauer(kurs.arbeitszeitProWoche)}</td>
                  <td className="zahl">{kurs.anzahlFahrten}</td>
                  <td>
                    {kurs.geteilterDienst && <span className="etikett orange">geteilter Dienst</span>}{' '}
                    {kurs.hatAbweichung && <span className="etikett rot">Arbeitszeit weicht ab</span>}
                  </td>
                  <td style={{ color: 'var(--grau-500)', fontSize: '0.82rem' }}>
                    {kurs.quellblatt ?? '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}
